import { CLIENT_APP_NAMES, CLIENT_APPS, SIGN_IN_LOG_APPS, type SignInLogApp } from "@seagames/contracts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AttemptRow } from "../../components/users/sign-ins";
import { errorMessage } from "../../lib/api/messages";
import { requireSuperAdmin } from "../../lib/auth/guards";
import { signInLogOptions } from "../../lib/users/queries";

/**
 * Users › Sign-in log (user, 2026-10-29): every sign-in attempt across every
 * login, newest first, with the app it came from — GMS, OVR, MSES, WSL
 * Notion, the web portal. SUPER_ADMIN (the API answers 403 otherwise). The
 * URL holds the filters; "Load more" walks the keyset, never an offset.
 */
export const Route = createFileRoute("/_app/users/sign-ins")({
	validateSearch: z.object({
		app: z.enum(SIGN_IN_LOG_APPS).optional().catch(undefined),
		success: z.enum(["true", "false"]).optional().catch(undefined),
	}),
	beforeLoad: ({ context }) => requireSuperAdmin(context.session),
	component: SignInLogPage,
});

function SignInLogPage() {
	const { t } = useTranslation(["users", "common"]);
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const q = useInfiniteQuery(signInLogOptions(search));
	const items = q.data?.pages.flatMap((p) => p.items) ?? [];

	const appLabel = (a: SignInLogApp) => (a === "UNKNOWN" ? t("signIns.unknownApp") : CLIENT_APP_NAMES[a]);
	const set = (patch: Partial<typeof search>) => navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

	return (
		<div className="flex flex-col gap-[1rem]">
			<div className="flex flex-wrap items-center gap-x-[1.25rem] gap-y-[0.5rem]">
				<Chips
					label={t("signIns.filterApp")}
					value={search.app}
					options={[...CLIENT_APPS, "UNKNOWN"].map((a) => ({ value: a as SignInLogApp, label: appLabel(a as SignInLogApp) }))}
					allLabel={t("signIns.allApps")}
					onChange={(app) => set({ app })}
				/>
				<Chips
					label={t("signIns.filterOutcome")}
					value={search.success}
					options={[
						{ value: "true" as const, label: t("signIns.reasons.OK") },
						{ value: "false" as const, label: t("signIns.refused") },
					]}
					allLabel={t("signIns.allOutcomes")}
					onChange={(success) => set({ success })}
				/>
			</div>

			<section className="rounded-box border border-gms-line bg-base-100 px-[1rem] py-[0.25rem]">
				{q.isPending ? (
					<p className="py-[1rem] text-sm text-base-content/55">{t("signIns.loading")}</p>
				) : q.isError ? (
					<div role="alert" className="my-[0.75rem] flex items-center justify-between gap-[1rem] rounded-field bg-error/8 px-[0.875rem] py-[0.75rem] text-sm">
						<span>{errorMessage(q.error)}</span>
						<button type="button" className="btn btn-xs btn-outline" onClick={() => void q.refetch()}>
							{t("common:action.retry")}
						</button>
					</div>
				) : items.length === 0 ? (
					<p className="py-[1.5rem] text-center text-sm text-base-content/55">{t("signIns.logEmpty")}</p>
				) : (
					<ul className="divide-y divide-base-300">
						{items.map((a) => (
							<AttemptRow key={a.id} a={a} />
						))}
					</ul>
				)}
			</section>

			{q.hasNextPage && (
				<button type="button" className="btn btn-sm btn-outline self-center" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
					{q.isFetchingNextPage && <span className="loading loading-spinner loading-xs" />}
					{t("signIns.loadMore")}
				</button>
			)}
		</div>
	);
}

/** One filter: "All" plus a chip per option; the pressed chip is the URL's value. */
function Chips<V extends string>({
	label,
	value,
	options,
	allLabel,
	onChange,
}: {
	label: string;
	value: V | undefined;
	options: ReadonlyArray<{ value: V; label: string }>;
	allLabel: string;
	onChange: (v: V | undefined) => void;
}) {
	const chip = (active: boolean) =>
		`btn btn-xs rounded-full ${active ? "btn-primary" : "btn-ghost border border-gms-line bg-base-100 font-medium"}`;
	return (
		<div role="group" aria-label={label} className="flex flex-wrap items-center gap-[0.375rem]">
			<span className="mr-[0.125rem] text-[0.75rem] font-semibold text-base-content/60">{label}</span>
			<button type="button" aria-pressed={value === undefined} className={chip(value === undefined)} onClick={() => onChange(undefined)}>
				{allLabel}
			</button>
			{options.map((o) => (
				<button key={o.value} type="button" aria-pressed={value === o.value} className={chip(value === o.value)} onClick={() => onChange(o.value)}>
					{o.label}
				</button>
			))}
		</div>
	);
}
